import './SearchItems.scss';
import { AMPLITUDE_EVENT } from '@sonar-software/sonar-analytics';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { NoData } from '@ui/NoData';
import { SearchItem } from './SearchItem';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { getSonarObjectTypeAndSourceLabelPlural, isSonarObjectTypeSearchAllowed } from '@sonar-software/sonar-ts-lib';
import { isSonarObjectSearchLoadingSelector, sonarObjectsSearchDataSelector } from './arch/selectors';
import React, { useCallback, useMemo } from 'react';
import { useAmplitude, usePeriscopeActions, useSonarSelector } from '@hooks';
export const targetObjectTypeOrder = [
    SonarObjectType.SalesforceEntity,
    SonarObjectType.SalesforceField,
    SonarObjectType.SalesforceWorkflowRule,
    SonarObjectType.SalesforceFieldUpdate,
    SonarObjectType.SalesforceWorkflowAlert,
    SonarObjectType.SalesforceValidationRule,
    SonarObjectType.SalesforceProcessBuilder,
    SonarObjectType.SalesforceFlow,
    SonarObjectType.SalesforceApexClass,
    SonarObjectType.SalesforceApexTrigger,
    SonarObjectType.SalesforcePageLayout,
    SonarObjectType.SalesforceReport,
    SonarObjectType.SalesforceEmailTemplate,
    SonarObjectType.PardotEntity,
    SonarObjectType.PardotField,
    SonarObjectType.PardotForm,
    SonarObjectType.PardotLandingPage,
    SonarObjectType.PardotList,
    SonarObjectType.PardotPageAction,
    SonarObjectType.PardotProfile,
    SonarObjectType.PardotAutomation,
    SonarObjectType.PardotEmailTemplate,
    SonarObjectType.PardotEmail
];
function getSortedDataKeys(data) {
    if (!data) {
        return [];
    }
    const keys = Object.keys(data).filter((item) => targetObjectTypeOrder.indexOf(item) > -1);
    return keys.sort((a, b) => targetObjectTypeOrder.indexOf(a) - targetObjectTypeOrder.indexOf(b));
}
export const SearchItems = ({ currentType, onTypeSelection }) => {
    const { startPeriscope } = usePeriscopeActions();
    const logEvent = useAmplitude();
    const isSearchLoading = useSonarSelector(isSonarObjectSearchLoadingSelector);
    const searchData = useSonarSelector(sonarObjectsSearchDataSelector);
    const searchDataKeys = useMemo(() => getSortedDataKeys(searchData), [searchData]);
    const handleClick = useCallback((item, type) => {
        const { displayName, sonarObjectId } = item;
        logEvent(AMPLITUDE_EVENT.SEARCH_RESULT_CLICKED, {
            type
        });
        startPeriscope({
            id: sonarObjectId,
            name: displayName,
            type
        });
    }, [logEvent, startPeriscope]);
    if (isSearchLoading) {
        return React.createElement(LoadingSpinner, { spinning: true });
    }
    let searchItems;
    if (!searchData || !searchDataKeys.length) {
        if (currentType) {
            searchItems =
                React.createElement("div", { className: 'header' },
                    React.createElement("span", null, getSonarObjectTypeAndSourceLabelPlural(currentType)),
                    React.createElement(NoData, { className: 'search-items-empty', imageName: 'default' }));
        }
        else {
            searchItems = React.createElement(NoData, { className: 'search-items-empty', imageName: 'default' });
        }
    }
    else {
        searchItems = searchDataKeys.filter(isSonarObjectTypeSearchAllowed).map((type) => {
            const items = searchData[type];
            const header = getSonarObjectTypeAndSourceLabelPlural(type);
            const showSeeMore = !currentType;
            return (React.createElement("div", { className: 'search-type-grouping', key: type },
                React.createElement("div", { className: 'header' },
                    React.createElement("span", null, header),
                    showSeeMore &&
                        React.createElement("span", { className: 'show-more-link', onClick: () => onTypeSelection(type) }, COPY_GENERIC.SHOW_MORE)),
                items.map((item) => React.createElement(SearchItem, { item: item, key: item.sonarObjectId, onClick: handleClick, type: type }))));
        });
    }
    return React.createElement("div", { id: 'search-items' }, searchItems);
};
